import { DEFAULT_CLINCH_THEME } from '@/theme/brandTheme'
import { isTenantWorkspaceHost } from '@/utils/saasPlatform'

/** Platform default PNG — not a tenant-uploaded logo. */
export const DEFAULT_CLINCH_LOGO_URL = DEFAULT_CLINCH_THEME.logoUrl

const CLINCH_LOGO_SUFFIXES = ['clinch-logo-refresh.png', 'clinch-logo']

/**
 * True when the tenant/workspace uploaded or set a custom logo URL.
 */
export function hasCustomBrandLogo(logoUrl) {
  const url = (logoUrl || '').trim()
  if (!url) return false
  if (url === DEFAULT_CLINCH_LOGO_URL) return false
  const lower = url.toLowerCase()
  return !CLINCH_LOGO_SUFFIXES.some((suffix) => lower.includes(suffix))
}

/**
 * First alphanumeric character of the display name (company / workspace / business).
 */
export function getBrandNameInitial(name) {
  const trimmed = (name || '').trim()
  if (!trimmed) return '?'
  const match = trimmed.match(/[\p{L}\p{N}]/u)
  return match ? match[0].toUpperCase() : '?'
}

function parseHexColor(color) {
  if (!color || typeof color !== 'string') return null
  const value = color.trim()
  if (!value.startsWith('#')) return null
  let hex = value.slice(1)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((ch) => ch + ch)
      .join('')
  }
  if (hex.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(hex)) return null
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  }
}

function parseRgbColor(color) {
  if (!color || typeof color !== 'string') return null
  const rgbMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
    }
  }
  return parseHexColor(color)
}

/** True for white / near-white fills (use dark text). */
export function isLightBackground(color) {
  const rgb = parseRgbColor(color)
  if (!rgb) return false
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
  return luminance >= 0.72
}

/** White on colored/dark backgrounds; navy on white/light backgrounds. */
export function contrastTextOnBackground(
  backgroundColor,
  { lightText = '#FFFFFF', darkText = '#001F3F' } = {}
) {
  return isLightBackground(backgroundColor) ? darkText : lightText
}

export function resolveThemePrimaryHex(fallback = '#33B5FF') {
  if (typeof document === 'undefined') return fallback
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--clinch-blue-primary')
    .trim()
  if (!raw) return fallback
  return raw.startsWith('#') ? raw : `#${raw}`
}

/**
 * Uploaded image on platform, or Clinch default PNG on platform apex.
 * Generated initial mark on workspace tenants when no custom logo.
 */
export function shouldShowBrandLogoMark(logoUrl, { tenantHost } = {}) {
  if (hasCustomBrandLogo(logoUrl)) return false
  const onTenant = tenantHost !== undefined ? tenantHost : isTenantWorkspaceHost()
  return onTenant
}

export function resolveBrandLogoImageUrl(logoUrl) {
  const url = (logoUrl || '').trim()
  if (hasCustomBrandLogo(url)) return url
  if (!shouldShowBrandLogoMark(url)) return url || DEFAULT_CLINCH_LOGO_URL
  return ''
}
